import React from "react";
import type { Employee, ExternalAgent, ExternalMessagingUser } from "../modules/employees/types";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import MaterialIcon from "@/components/ui/MaterialIcon";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

type EmployeeCardProps = {
  employee: Employee;
  employees: Employee[];
  managerId: string | null;
  included: boolean;
  notifyManager: boolean;
  externalAgentId: string | null;
  externalMessagingId: string | null;
  externalAgents: ExternalAgent[];
  messagingUsers: ExternalMessagingUser[];
  assignedAgentIds?: Map<string, string>;
  assignedMessagingIds?: Map<string, string>;
  externalLoading: boolean;
  externalAgentDirty?: boolean;
  externalMessagingDirty?: boolean;
  onManagerChange?: (employeeId: string, managerId: string | null) => void;
  onIncludedChange?: (employeeId: string, included: boolean) => void;
  onNotifyManagerChange?: (employeeId: string, notifyManager: boolean) => void;
  onExternalAgentChange?: (employeeId: string, id: string | null) => void;
  onExternalMessagingChange?: (employeeId: string, id: string | null) => void;
};

const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  employees,
  managerId,
  included,
  notifyManager,
  externalAgentId,
  externalMessagingId,
  externalAgents,
  messagingUsers,
  assignedAgentIds,
  assignedMessagingIds,
  externalLoading,
  externalAgentDirty = false,
  externalMessagingDirty = false,
  onManagerChange,
  onIncludedChange,
  onNotifyManagerChange,
  onExternalAgentChange,
  onExternalMessagingChange,
}) => {
  const handleManagerChange = (value: string) => {
    const newManagerId = value === "none" ? null : value;
    onManagerChange?.(employee.id, newManagerId);
  };

  const handleIncludedChange = (checked: boolean) => {
    onIncludedChange?.(employee.id, checked);
  };

  const handleNotifyManagerChange = (checked: boolean) => {
    onNotifyManagerChange?.(employee.id, checked);
  };

  const handleExternalAgentChange = (value: string) => {
    onExternalAgentChange?.(employee.id, value === "none" ? null : value);
  };

  const handleExternalMessagingChange = (value: string) => {
    onExternalMessagingChange?.(employee.id, value === "none" ? null : value);
  };

  const renderOptionLabel = (name: string, email: string, isMatch: boolean) => (
    <span className="flex items-center gap-1.5">
      {isMatch && <MaterialIcon icon="link" size={14} className="text-muted-foreground shrink-0" />}
      <span>{name} ({email})</span>
    </span>
  );

  return (
    <Card
      className={`py-4 ${
        included ? "border-border" : "text-muted-foreground"
      }`}
    >
      <div className="grid grid-cols-[auto_1fr_1.5fr_1.5fr_auto_1fr_auto] items-center px-6 gap-4">
        {/* Include Switch */}
        <div className="flex items-center justify-center w-16">
          <Switch
            size="lg"
            checked={included}
            onCheckedChange={handleIncludedChange}
            aria-label={`Include ${employee.name} in scoring`}
          />
        </div>

        {/* Name and Email */}
        <div className="min-w-0">
          <Text
            as="div"
            variant="md"
            className={`font-semibold leading-none truncate ${
              !included ? "text-muted-foreground" : ""
            }`}
          >
            {employee.name}
          </Text>
          <Text
            as="div"
            variant="sm"
            className={`mt-1 truncate ${
              included ? "text-muted-foreground" : "text-muted-foreground"
            }`}
          >
            {employee.email}
          </Text>
        </div>

        {/* Agent ID (e.g. Intercom) */}
        <div className="min-w-0">
          <Select
            value={externalAgentId || "none"}
            onValueChange={handleExternalAgentChange}
            disabled={externalLoading}
          >
            <SelectTrigger className="w-full bg-muted/50 border-muted-foreground/20" dirty={externalAgentDirty}>
              <SelectValue placeholder="Not assigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">Not assigned</span>
              </SelectItem>
              {externalAgents.length > 0 && <SelectSeparator />}
              {(() => {
                const sorted = [...externalAgents].sort((a, b) => a.name.localeCompare(b.name));
                const available = sorted.filter((agent) => {
                  const assignedTo = assignedAgentIds?.get(agent.id);
                  return !(assignedTo !== undefined && assignedTo !== employee.id);
                });
                const assigned = sorted.filter((agent) => {
                  const assignedTo = assignedAgentIds?.get(agent.id);
                  return assignedTo !== undefined && assignedTo !== employee.id;
                });
                return (
                  <>
                    {available.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {renderOptionLabel(agent.name, agent.email, agent.email === employee.email)}
                      </SelectItem>
                    ))}
                    {assigned.length > 0 && available.length > 0 && <SelectSeparator />}
                    {assigned.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Already assigned</SelectLabel>
                        {assigned.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <span className="text-muted-foreground/50">
                              {renderOptionLabel(agent.name, agent.email, agent.email === employee.email)}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </>
                );
              })()}
            </SelectContent>
          </Select>
        </div>

        {/* Messaging ID (e.g. Slack) */}
        <div className="min-w-0">
          <Select
            value={externalMessagingId ?? "none"}
            onValueChange={handleExternalMessagingChange}
            disabled={externalLoading}
          >
            <SelectTrigger className="w-full bg-muted/50 border-muted-foreground/20" dirty={externalMessagingDirty}>
              <SelectValue placeholder="Not assigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">Not assigned</span>
              </SelectItem>
              {messagingUsers.length > 0 && <SelectSeparator />}
              {(() => {
                const sorted = [...messagingUsers].sort((a, b) => a.name.localeCompare(b.name));
                const available = sorted.filter((user) => {
                  const assignedTo = assignedMessagingIds?.get(user.id);
                  return !(assignedTo !== undefined && assignedTo !== employee.id);
                });
                const assigned = sorted.filter((user) => {
                  const assignedTo = assignedMessagingIds?.get(user.id);
                  return assignedTo !== undefined && assignedTo !== employee.id;
                });
                return (
                  <>
                    {available.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {renderOptionLabel(user.name, user.email, user.email === employee.email)}
                      </SelectItem>
                    ))}
                    {assigned.length > 0 && available.length > 0 && <SelectSeparator />}
                    {assigned.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Already assigned</SelectLabel>
                        {assigned.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <span className="text-muted-foreground/50">
                              {renderOptionLabel(user.name, user.email, user.email === employee.email)}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </>
                );
              })()}
            </SelectContent>
          </Select>
        </div>

        {/* Column separator */}
        <div className="w-px self-stretch bg-border" />

        {/* Manager Select */}
        <div className="min-w-0">
          {included ? (
            <Select
              value={managerId ?? "none"}
              onValueChange={handleManagerChange}
            >
              <SelectTrigger className="w-full bg-muted/50 border-muted-foreground/20">
                <SelectValue placeholder="Select manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">No manager</span>
                </SelectItem>
                {employees
                  .filter((emp) => emp.id !== employee.id)
                  .map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        {/* Notify Manager Switch */}

        <div className={`w-24 flex items-center gap-0 justify-end ${!included ? "invisible" : ""}`}>
        {managerId && (
          <>
          <Switch
            id={`notify-manager-${employee.id}`}
            checked={notifyManager}
            onCheckedChange={handleNotifyManagerChange}
            disabled={!included}
          />
          <Label htmlFor={`notify-manager-${employee.id}`} className="pl-2 text-sm text-muted-foreground cursor-pointer">
            Notify
          </Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center text-muted-foreground cursor-default pl-2">
                <MaterialIcon icon="info" size={16} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Send scores for {employee.name} to {employees.find((e) => e.id === managerId)?.name ?? "their manager"} too
            </TooltipContent>
          </Tooltip>
          </>)}
        </div>
      </div>
    </Card>
  );
};

export default EmployeeCard;
